<template>
  <TransitionRoot as="template" :show="open">
    <Dialog as="div" class="relative z-50" @close="$emit('close')">
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
      </TransitionChild>

      <div class="fixed inset-0 z-10 overflow-y-auto">
        <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl">
              <div class="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                <div class="sm:flex sm:items-start">
                  <div class="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
                    <DialogTitle as="h3" class="text-xl font-semibold leading-6 text-gray-900">
                      {{ props.existingCourse ? 'Re-process Course Package' : (step === 1 ? 'Register Your Course' : step === 2 ? 'Integration Method' : 'Get Your Code') }}
                    </DialogTitle>
                    
                    <!-- Step 1: Integration Method Selection -->
                    <div v-if="step === 1" class="mt-4">
                      <label class="block text-sm font-medium text-gray-700 mb-2">
                        {{ props.existingCourse ? 'Select your updated SCORM/xAPI package to re-process:' : 'How will you add Happy Alien to your course?' }}
                      </label>
                      <div v-if="!props.existingCourse" class="grid grid-cols-2 gap-3">
                        <button
                          @click="selectIntegrationMethod('upload')"
                          :class="[
                            'relative rounded-lg border p-4 focus:outline-none',
                            integrationMethod === 'upload'
                              ? 'border-teal-500 ring-2 ring-teal-500'
                              : 'border-gray-300 hover:border-gray-400'
                          ]"
                        >
                          <PhPackage class="w-8 h-8 mx-auto mb-2 text-primary" weight="fill" />
                          <div class="text-sm font-medium">Process Package Locally</div>
                          <div class="text-xs text-gray-500 mt-1">Inject script & download modified version</div>
                        </button>
                        <button
                          @click="selectIntegrationMethod('manual')"
                          :class="[
                            'relative rounded-lg border p-4 focus:outline-none',
                            integrationMethod === 'manual'
                              ? 'border-teal-500 ring-2 ring-teal-500'
                              : 'border-gray-300 hover:border-gray-400'
                          ]"
                        >
                          <PhClipboardText class="w-8 h-8 mx-auto mb-2 text-primary" weight="fill" />
                          <div class="text-sm font-medium">Copy/Paste Script</div>
                          <div class="text-xs text-gray-500 mt-1">Add to your course manually</div>
                        </button>
                      </div>
                    </div>
                    
                    <!-- Step 2A: Course Info (Manual) -->
                    <div v-if="step === 2 && integrationMethod === 'manual'" class="mt-4">
                      <div class="mb-4">
                        <label for="course-title" class="block text-sm font-medium text-gray-700">
                          Course Title
                        </label>
                        <input
                          id="course-title"
                          v-model="courseTitle"
                          type="text"
                          placeholder="e.g., 2024 Workplace Safety Training"
                          @input="generateCourseId"
                          class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 sm:text-sm"
                        />
                        <p v-if="courseId" class="mt-1 text-xs text-gray-500">
                          Course ID: {{ courseId }}
                        </p>
                      </div>
                      
                      <div>
                        <label class="block text-sm font-medium text-gray-700 mb-2">
                          Select Your Authoring Tool
                        </label>
                        <div class="grid grid-cols-2 gap-2">
                          <button
                            v-for="tool in tools"
                            :key="tool.id"
                            @click="selectedTool = tool.id"
                            :class="[
                              'p-3 rounded-lg border text-left hover:bg-gray-50',
                              selectedTool === tool.id
                                ? 'border-teal-500 ring-2 ring-teal-500'
                                : 'border-gray-300'
                            ]"
                          >
                            <div class="font-medium text-sm">{{ tool.name }}</div>
                            <div class="text-xs text-gray-500">{{ tool.type }}</div>
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    <!-- Step 2B: Package Upload -->
                    <div v-if="(step === 2 && integrationMethod === 'upload') || (step === 1 && integrationMethod === 'upload') || (step === 1 && props.existingCourse)" class="mt-4">
                      <div v-if="!selectedFile" class="text-sm text-gray-600 mb-4">
                        Select your eLearning package (SCORM, xAPI/TinCan, or cmi5). We'll process it locally in your browser, inject our script, and provide a modified version to download.
                      </div>
                      <div v-if="selectedFile">
                        <div class="mb-4">
                          <label for="course-title-upload" class="block text-sm font-medium text-gray-700">
                            Course Title
                          </label>
                          <input
                            id="course-title-upload"
                            v-model="courseTitle"
                            type="text"
                            placeholder="Enter course title or use extracted title"
                            @input="generateCourseId"
                            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 sm:text-sm"
                          />
                          <p v-if="courseId" class="mt-1 text-xs text-gray-500">
                            Course ID: {{ courseId }}
                          </p>
                        </div>
                        <div class="text-sm text-gray-600 mb-2">
                          We'll inject this script into your package (processed entirely in your browser):
                        </div>
                        <pre class="bg-gray-900 text-green-400 p-3 rounded text-xs overflow-x-auto mb-4">{{ getHtmlScript() }}</pre>
                      </div>
                      
                      <div
                        class="mt-4 border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors"
                        :class="{ 'border-blue-400 bg-blue-50': isDragging }"
                        @dragover.prevent="isDragging = true"
                        @dragleave.prevent="isDragging = false"
                        @drop.prevent="handleFileDrop"
                      >
                        <input
                          ref="fileInput"
                          type="file"
                          accept=".zip"
                          @change="handleFileSelect"
                          class="hidden"
                        >
                        
                        <div v-if="!selectedFile">
                          <div class="text-gray-400 mb-2">
                            <svg class="mx-auto h-12 w-12" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                              <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
                            </svg>
                          </div>
                          <button @click="$refs.fileInput.click()" class="text-teal-600 hover:text-teal-500 font-medium">
                            Choose SCORM package
                          </button>
                          <span class="text-gray-500"> or drag and drop</span>
                          <p class="text-xs text-gray-500 mt-2">ZIP files processed locally - nothing uploaded to servers</p>
                        </div>
                        
                        <div v-else class="space-y-3">
                          <div class="flex items-center justify-center space-x-3">
                            <svg class="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                            </svg>
                            <div>
                              <div class="font-medium text-gray-900">{{ selectedFile.name }}</div>
                              <div class="text-sm text-gray-500">{{ formatFileSize(selectedFile.size) }}</div>
                            </div>
                          </div>
                          <button 
                            @click="clearSelectedFile"
                            class="text-sm text-gray-500 hover:text-gray-700 underline"
                          >
                            Choose different file
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    <!-- Step 3: Get Code (Manual) -->
                    <div v-if="step === 3 && integrationMethod === 'manual'" class="mt-4">
                      <!-- JavaScript Code for Storyline/Captivate -->
                      <div v-if="['storyline', 'captivate', 'lectora'].includes(selectedTool)">
                        <div class="text-sm text-gray-600 mb-2">
                          Copy this JavaScript code:
                        </div>
                        <pre class="bg-gray-900 text-green-400 p-3 rounded text-xs overflow-x-auto">{{ getJavaScriptCode() }}</pre>
                        
                        <div class="mt-4 p-3 bg-blue-50 rounded-lg">
                          <h4 class="font-medium text-sm mb-2 flex items-center"><PhMapPin class="w-4 h-4 mr-1.5" weight="fill" /> How to add this code:</h4>
                          
                          <div v-if="selectedTool === 'storyline'" class="text-sm">
                            <ol class="list-decimal list-inside space-y-1">
                              <li>Open your project in Storyline</li>
                              <li>View → Slide Master</li>
                              <li>Select the topmost master slide</li>
                              <li>Insert → Trigger → Execute JavaScript</li>
                              <li>When: Timeline starts</li>
                              <li>Paste the code and click OK</li>
                            </ol>
                            <div class="mt-2 p-2 bg-yellow-50 rounded text-xs flex items-center">
                              <PhWarning class="w-4 h-4 mr-1.5 text-yellow-600 flex-shrink-0" weight="fill" /> <strong>Important:</strong> Add to Slide Master, not individual slides
                            </div>
                          </div>
                          
                          <div v-if="selectedTool === 'captivate'" class="text-sm">
                            <ol class="list-decimal list-inside space-y-1">
                              <li>Open your project in Captivate</li>
                              <li>Select the first slide</li>
                              <li>Window → Actions</li>
                              <li>On Enter → Execute JavaScript</li>
                              <li>Paste the code and click OK</li>
                            </ol>
                          </div>
                          
                          <div v-if="selectedTool === 'lectora'" class="text-sm">
                            <ol class="list-decimal list-inside space-y-1">
                              <li>Open your project in Lectora</li>
                              <li>Go to Title level</li>
                              <li>Add Action → Run JavaScript</li>
                              <li>Paste the code</li>
                            </ol>
                          </div>
                        </div>
                      </div>
                      
                      <!-- HTML Script for LMS -->
                      <div v-else-if="['lms', 'other'].includes(selectedTool)">
                        <div class="text-sm text-gray-600 mb-2">
                          Copy this HTML script tag:
                        </div>
                        <pre class="bg-gray-900 text-green-400 p-3 rounded text-xs overflow-x-auto">{{ getHtmlScript() }}</pre>
                        
                        <div class="mt-4 p-3 bg-blue-50 rounded-lg text-sm">
                          <p>Add this script to your LMS custom scripts section or HTML header.</p>
                        </div>
                      </div>
                      
                      <!-- Rise Warning -->
                      <div v-else-if="selectedTool === 'rise'">
                        <div class="p-4 bg-yellow-50 border-2 border-yellow-200 rounded">
                          <h4 class="font-medium text-yellow-900 mb-2 flex items-center"><PhWarning class="w-5 h-5 mr-1.5" weight="fill" /> Articulate Rise Limitation</h4>
                          <p class="text-sm mb-3">Rise doesn't support custom JavaScript injection.</p>
                          <p class="text-sm font-medium">Please use one of these options:</p>
                          <ol class="list-decimal list-inside text-sm space-y-1 mt-2">
                            <li>Export your Rise course as SCORM</li>
                            <li>Use our "Process Package" option to inject the script locally</li>
                          </ol>
                        </div>
                      </div>
                    </div>
                    
                    <!-- Processing/Success State -->
                    <div v-if="processing || success" class="mt-4">
                      <!-- Processing State -->
                      <div v-if="processing && !success" class="p-4 bg-blue-50 rounded-lg">
                        <div class="flex items-center">
                          <svg class="animate-spin h-5 w-5 text-blue-600 mr-3" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <div>
                            <div class="font-medium text-blue-900">{{ processingMessage }}</div>
                            <div class="text-sm text-blue-700">{{ processingDetail }}</div>
                          </div>
                        </div>
                        <div v-if="processingSteps.length > 0" class="mt-3 space-y-1">
                          <div v-for="step in processingSteps" :key="step" class="text-xs text-blue-600 flex items-center">
                            <svg class="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                            </svg>
                            {{ step }}
                          </div>
                        </div>
                      </div>
                      
                      <!-- Success State -->
                      <div v-if="success" class="p-4 bg-green-50 rounded-lg">
                        <div class="flex items-start">
                          <svg class="h-5 w-5 text-green-600 mr-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                          </svg>
                          <div class="flex-1">
                            <div class="font-medium text-green-900">Success! Your Package is Ready</div>
                            <div class="text-sm text-green-700 mt-2">
                              <div class="mb-2">{{ successMessage }}</div>
                              <div class="space-y-1">
                                <div class="flex items-center"><PhCheckCircle class="w-4 h-4 mr-1.5 flex-shrink-0" weight="fill" /> Course registered in Happy Alien dashboard</div>
                                <div class="flex items-center"><PhCheckCircle class="w-4 h-4 mr-1.5 flex-shrink-0" weight="fill" /> Script injected into your package</div>
                                <div class="flex items-center"><PhCheckCircle class="w-4 h-4 mr-1.5 flex-shrink-0" weight="fill" /> Modified package downloaded automatically</div>
                              </div>
                            </div>
                            <div class="mt-3 p-2 bg-green-100 rounded text-xs text-green-800">
                              <div class="font-medium mb-1 flex items-center"><PhDownloadSimple class="w-4 h-4 mr-1.5" weight="fill" /> Downloaded to your computer:</div>
                              <div class="font-mono">{{ courseId }}-happy-alien-enabled.zip</div>
                            </div>
                            <div class="text-xs text-green-600 mt-2 flex items-center">
                              <PhLock class="w-4 h-4 mr-1.5 flex-shrink-0" weight="fill" /> Your package was processed entirely in your browser - no files were uploaded to our servers
                            </div>
                          </div>
                        </div>
                        
                        <div v-if="modifiedPackage && false" class="mt-4 space-y-3">
                          <!-- Download button removed - auto-download happens now -->
                          <button
                            @click="downloadPackage"
                            class="w-full bg-green-600 text-white px-4 py-3 rounded-lg hover:bg-green-700 flex items-center justify-center font-medium"
                          >
                            <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"></path>
                            </svg>
                            Download Modified Package
                          </button>
                          
                          <div class="p-3 bg-blue-50 rounded text-sm">
                            <div class="flex items-start">
                              <svg class="w-5 h-5 text-blue-600 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
                              </svg>
                              <div class="text-blue-900">
                                <strong>What to do next:</strong>
                                <ol class="list-decimal list-inside mt-1 space-y-1 text-xs">
                                  <li>Find the modified package in your Downloads folder</li>
                                  <li>Upload it to your LMS (Moodle, Canvas, etc.) just like any SCORM package</li>
                                  <li>Your course will now have Happy Alien features enabled!</li>
                                  <li>Manage features from the Courses dashboard anytime</li>
                                </ol>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- Footer Buttons -->
              <div class="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                <!-- Step 1: Continue -->
                <button
                  v-if="step === 1"
                  @click="handleNext"
                  :disabled="!integrationMethod"
                  type="button"
                  class="inline-flex w-full justify-center rounded-md bg-teal-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-teal-500 sm:ml-3 sm:w-auto disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Continue
                </button>
                
                <!-- Step 2 Manual: Get Code -->
                <button
                  v-if="step === 2 && integrationMethod === 'manual' && !processing && !success"
                  @click="handleNext"
                  :disabled="!courseTitle || !selectedTool"
                  type="button"
                  class="inline-flex w-full justify-center rounded-md bg-teal-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-teal-500 sm:ml-3 sm:w-auto disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Get Code
                </button>
                
                <!-- Step 2 Upload: Process Package -->
                <button
                  v-if="((step === 2 && integrationMethod === 'upload') || (step === 1 && props.existingCourse)) && !processing && !success"
                  @click="processPackage"
                  :disabled="!selectedFile || !courseTitle"
                  type="button"
                  class="inline-flex w-full justify-center rounded-md bg-teal-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-teal-500 sm:ml-3 sm:w-auto disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Process Package
                </button>
                
                <!-- Step 3 Manual: Copy Code -->
                <button
                  v-if="step === 3 && integrationMethod === 'manual'"
                  @click="copyCode"
                  type="button"
                  class="inline-flex w-full justify-center rounded-md bg-teal-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-teal-500 sm:ml-3 sm:w-auto"
                >
                  Copy Code
                </button>
                
                <!-- Back button -->
                <button
                  v-if="step > 1 && !processing && !success"
                  @click="handleBack"
                  type="button"
                  class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                >
                  Back
                </button>
                
                <!-- Cancel/Done button -->
                <button
                  @click="handleClose"
                  type="button"
                  :disabled="processing"
                  :class="[
                    'mt-3 inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold shadow-sm sm:mt-0 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed',
                    success 
                      ? 'bg-green-600 text-white hover:bg-green-500' 
                      : 'bg-white text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50'
                  ]"
                >
                  {{ success ? 'Done' : 'Cancel' }}
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue'
import {
  PhPackage,
  PhClipboardText,
  PhMapPin,
  PhWarning,
  PhCheckCircle,
  PhDownloadSimple,
  PhLock,
} from '@phosphor-icons/vue'
import { useAuthStore } from '../stores/auth'
import JSZip from 'jszip'

const props = defineProps<{
  open: boolean
  orgId?: string
  existingCourse?: {
    id: string
    course_id: string
    title: string
    tool_type?: string
    integration_method: string
  }
}>()

const emit = defineEmits<{
  close: []
  success: [data: { courseId: string, title: string, method: string, toolType?: string }]
}>()

const authStore = useAuthStore()

// State
const step = ref(1)
const courseTitle = ref('')
const courseId = ref('')
const integrationMethod = ref('')
const selectedTool = ref('')
const selectedFile = ref<File | null>(null)
const isDragging = ref(false)
const processing = ref(false)
const success = ref(false)
const successMessage = ref('')
const modifiedPackage = ref<Blob | null>(null)
const filesModified = ref(0)
const processingMessage = ref('Processing SCORM package...')
const processingDetail = ref('This may take a few moments')
const processingSteps = ref<string[]>([])
const hasDownloaded = ref(false)

// Tools list
const tools = [
  { id: 'storyline', name: 'Articulate Storyline', type: 'JavaScript' },
  { id: 'captivate', name: 'Adobe Captivate', type: 'JavaScript' },
  { id: 'lectora', name: 'Lectora', type: 'JavaScript' },
  { id: 'rise', name: 'Articulate Rise', type: 'Not Supported' },
  { id: 'lms', name: 'LMS/Moodle', type: 'HTML Script' },
  { id: 'other', name: 'Other/Custom', type: 'HTML Script' }
]

// Computed
const orgIdValue = computed(() => props.orgId || authStore.user?.organization_id || authStore.user?.id || 'demo-org')

// Watch for existing course prop
watch(() => props.existingCourse, (course) => {
  if (course) {
    // Pre-fill with existing course data for re-upload
    courseTitle.value = course.title
    courseId.value = course.course_id
    integrationMethod.value = 'upload' // Force upload method for re-processing
    selectedTool.value = course.tool_type || ''
    step.value = 1 // Start at step 1 for re-upload
  }
}, { immediate: true })

// Methods
function selectIntegrationMethod(method: string) {
  integrationMethod.value = method
  if (method === 'upload') {
    // For upload, we'll get the title from the package
    courseTitle.value = ''
    courseId.value = ''
  }
}

function generateCourseId() {
  if (!courseTitle.value) return
  
  const slug = courseTitle.value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .substring(0, 30)
  
  const suffix = Math.random().toString(36).substring(2, 6)
  courseId.value = `${slug}-${suffix}`
}

function getJavaScriptCode() {
  return `(function() {
  // Happy Alien Universal Loader
  // Course: ${courseTitle.value}
  // ID: ${courseId.value}
  
  // Prevent multiple loads (important for Slide Masters)
  if (window.happyAlienLoaded) return;
  window.happyAlienLoaded = true;
  
  var script = document.createElement('script');
  script.id = 'happyalien-loader';
  script.setAttribute('data-org', '${orgIdValue.value}');
  script.setAttribute('data-course', '${courseId.value}');
  script.src = 'https://api.happyalien.ai/loader.js';
  
  // Add load confirmation
  script.onload = function() {
    console.log('[OK] Happy Alien loaded for: ${courseTitle.value}');
  };
  
  document.head.appendChild(script);
})();`
}

function getHtmlScript() {
  return `<!-- Happy Alien Universal Loader -->
<!-- Course: ${courseTitle.value} -->
<${'script'} id="happyalien-loader" 
  data-org="${orgIdValue.value}" 
  data-course="${courseId.value}"
  src="https://api.happyalien.ai/loader.js">
</${'script'}>`
}

async function handleNext() {
  if (step.value === 1) {
    // For upload method, check if file is selected
    if (integrationMethod.value === 'upload') {
      if (!selectedFile.value) {
        alert('Please select a SCORM/xAPI package file to continue')
        return
      }
      
      // Wait a moment for file processing to complete if needed
      if (!courseTitle.value) {
        alert('Please wait for the file to be processed or enter a course title manually')
        return
      }
    }
    
    // For manual method, just check integration method is selected
    if (integrationMethod.value === 'manual') {
      // Move to step 2 where they'll enter the title
      step.value = 2
      return
    }
    
    // For upload method, generate courseId if not already set
    if (!courseId.value && courseTitle.value) {
      generateCourseId()
    }
    
    // Validate required fields for upload method
    if (!courseId.value || !courseTitle.value) {
      console.error('Course ID and title are required')
      alert('Please select a package file first')
      return
    }
    
    // Register course for upload method
    await registerCourse()
    step.value = 2
  } else if (step.value === 2 && integrationMethod.value === 'manual') {
    // Generate courseId if not already set
    if (!courseId.value && courseTitle.value) {
      generateCourseId()
    }
    
    // Validate required fields
    if (!courseId.value || !courseTitle.value) {
      console.error('Course ID and title are required')
      alert('Please enter a course title')
      return
    }
    
    // Register course
    await registerCourse()
    step.value = 3
  }
}

function handleBack() {
  if (step.value > 1) {
    step.value--
  }
}

async function registerCourse() {
  // Use the Sparks API for course registration
  const apiUrl = import.meta.env.DEV 
    ? 'http://localhost:3001' 
    : 'https://api.happyalien.ai';
  
  // Get auth token from user
  const authToken = authStore.accessToken || '';
  
  try {
    const response = await fetch(`${apiUrl}/courses`, {
      method: 'POST',
      credentials: 'include',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      },
      body: JSON.stringify({
        course_id: courseId.value,
        title: courseTitle.value,
        platform: selectedTool.value || 'custom',
        metadata: {
          tool_type: selectedTool.value || 'custom',
          integration_method: integrationMethod.value
        }
      })
    })
    
    if (!response.ok && response.status !== 401) {
      console.warn('Course registration API not available, continuing with local storage')
    }
  } catch (error) {
    console.log('Course registration API not available yet, continuing with local storage')
  }
}

function handleFileDrop(event: DragEvent) {
  isDragging.value = false
  const files = event.dataTransfer?.files
  if (files && files.length > 0) {
    handleFile(files[0])
  }
}

function handleFileSelect(event: Event) {
  const target = event.target as HTMLInputElement
  if (target.files && target.files.length > 0) {
    handleFile(target.files[0])
  }
}

async function handleFile(file: File) {
  if (!file.name.toLowerCase().endsWith('.zip')) {
    alert('Please select a ZIP file')
    return
  }
  selectedFile.value = file
  
  // Try to extract course title from the package
  try {
    const zip = new JSZip()
    const contents = await zip.loadAsync(file)
    
    // Check for SCORM manifest (imsmanifest.xml)
    const scormManifest = contents.files['imsmanifest.xml']
    if (scormManifest) {
      // @ts-ignore
      const manifestContent = await scormManifest.async('string')
      
      // Extract title from SCORM manifest
      const titleMatch = manifestContent.match(/<title>([^<]+)<\/title>/i)
      if (titleMatch && titleMatch[1]) {
        courseTitle.value = titleMatch[1].trim()
        generateCourseId()
      }
    }
    
    // Check for xAPI (TinCan) manifest (tincan.xml)
    if (!courseTitle.value) {
      const xapiManifest = contents.files['tincan.xml']
      if (xapiManifest) {
        // @ts-ignore
        const manifestContent = await xapiManifest.async('string')
        
        // Extract title from xAPI manifest - look for activity name
        const nameMatch = manifestContent.match(/<name>([^<]+)<\/name>/i)
        if (nameMatch && nameMatch[1]) {
          courseTitle.value = nameMatch[1].trim()
          generateCourseId()
        } else {
          // Try alternate xAPI title format
          const titleMatch = manifestContent.match(/<title>([^<]+)<\/title>/i)
          if (titleMatch && titleMatch[1]) {
            courseTitle.value = titleMatch[1].trim()
            generateCourseId()
          }
        }
      }
    }
    
    // Check for cmi5 manifest (cmi5.xml)
    if (!courseTitle.value) {
      const cmi5Manifest = contents.files['cmi5.xml']
      if (cmi5Manifest) {
        // @ts-ignore
        const manifestContent = await cmi5Manifest.async('string')
        
        // Extract title from cmi5 manifest
        const titleMatch = manifestContent.match(/<title>([^<]+)<\/title>/i)
        if (titleMatch && titleMatch[1]) {
          courseTitle.value = titleMatch[1].trim()
          generateCourseId()
        }
      }
    }
    
    // If no title found in manifests, try to extract from HTML files
    if (!courseTitle.value) {
      // Common launch file names for different standards
      const launchFiles = ['index.html', 'index_lms.html', 'story.html', 'launch.html', 'start.html']
      
      for (const launchFile of launchFiles) {
        if (contents.files[launchFile]) {
          // @ts-ignore
          const content = await contents.files[launchFile].async('string')
          const titleMatch = content.match(/<title>([^<]+)<\/title>/i)
          if (titleMatch && titleMatch[1]) {
            courseTitle.value = titleMatch[1].trim()
            generateCourseId()
            break
          }
        }
      }
    }
    
    // If still no title, check any HTML file
    if (!courseTitle.value) {
      for (const [path, zipFile] of Object.entries(contents.files)) {
        // @ts-ignore
        if (path.match(/\.(html?|htm)$/i) && !zipFile.dir) {
          // @ts-ignore
          const content = await zipFile.async('string')
          const titleMatch = content.match(/<title>([^<]+)<\/title>/i)
          if (titleMatch && titleMatch[1]) {
            courseTitle.value = titleMatch[1].trim()
            generateCourseId()
            break
          }
        }
      }
    }
    
    // If still no title, use the filename
    if (!courseTitle.value) {
      courseTitle.value = file.name.replace(/\.zip$/i, '').replace(/[-_]/g, ' ')
      generateCourseId()
    }
  } catch (error) {
    console.error('Failed to extract course title:', error)
    // Fallback to filename
    courseTitle.value = file.name.replace(/\.zip$/i, '').replace(/[-_]/g, ' ')
    generateCourseId()
  }
}

function clearSelectedFile() {
  selectedFile.value = null
  const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement
  if (fileInput) {
    fileInput.value = ''
  }
}

async function processPackage() {
  if (!selectedFile.value || !courseTitle.value) return
  
  processing.value = true
  processingSteps.value = []
  processingMessage.value = 'Processing eLearning package...'
  processingDetail.value = 'Loading package contents'
  
  try {
    const zip = new JSZip()
    
    processingSteps.value.push('Loading ZIP file')
    const contents = await zip.loadAsync(selectedFile.value)
    
    // Detect package type
    let packageType = 'Unknown'
    if (contents.files['imsmanifest.xml']) {
      packageType = 'SCORM'
    } else if (contents.files['tincan.xml']) {
      packageType = 'xAPI/TinCan'
    } else if (contents.files['cmi5.xml']) {
      packageType = 'cmi5'
    } else if (contents.files['index.html'] || contents.files['story.html']) {
      packageType = 'HTML5'
    }
    
    processingSteps.value.push(`Detected ${packageType} package`)
    processingDetail.value = 'Scanning for HTML files'
    
    // Count HTML files
    let htmlFiles = 0
    for (const [path, file] of Object.entries(contents.files)) {
      // @ts-ignore
      if (path.match(/\.(html?|htm)$/i) && !file.dir) {
        htmlFiles++
      }
    }
    
    processingDetail.value = `Found ${htmlFiles} HTML file${htmlFiles !== 1 ? 's' : ''} to process`
    await new Promise(resolve => setTimeout(resolve, 500)) // Small delay for UX
    
    const loaderScript = getHtmlScript()
    filesModified.value = 0
    
    processingMessage.value = 'Injecting Happy Alien script...'
    processingSteps.value.push('Injecting loader script')
    
    const modifiedFilesList: string[] = []
    
    // Process each HTML file
    for (const [path, file] of Object.entries(contents.files)) {
      // @ts-ignore - file has async method
      if (path.match(/\.(html?|htm)$/i) && !file.dir) {
        const fileName = path.split('/').pop() || path
        processingDetail.value = `Processing: ${fileName}`
        
        // @ts-ignore
        let content = await file.async('string')
        
        // Check if already has loader
        if (content.includes('happyalien-loader')) {
          processingSteps.value.push(`✓ ${fileName} (already has loader)`)
        } else {
          // Find best injection point
          const bodyEnd = '<' + '/body>'
          const headEnd = '<' + '/head>'
          const bodyStart = '<body'
          
          let injected = false
          if (content.includes(bodyEnd)) {
            content = content.replace(bodyEnd, loaderScript + '\n' + bodyEnd)
            injected = true
          } else if (content.includes(headEnd)) {
            content = content.replace(headEnd, loaderScript + '\n' + headEnd)
            injected = true
          } else if (content.includes(bodyStart)) {
            content = content.replace(/<body[^>]*>/, (match) => match + '\n' + loaderScript)
            injected = true
          } else {
            // No standard injection point found, append to end
            content += '\n' + loaderScript
            injected = true
          }
          
          if (injected) {
            zip.file(path, content)
            filesModified.value++
            modifiedFilesList.push(fileName)
            processingSteps.value.push(`✓ Modified ${fileName}`)
          }
        }
        
        // Small delay for UX
        await new Promise(resolve => setTimeout(resolve, 100))
      }
    }
    
    processingSteps.value.push(`Modified ${filesModified.value} file${filesModified.value !== 1 ? 's' : ''}`)
    processingMessage.value = 'Generating modified package...'
    processingDetail.value = 'Creating downloadable ZIP file'
    
    // Generate modified package
    modifiedPackage.value = await zip.generateAsync({ 
      type: 'blob',
      compression: 'DEFLATE',
      compressionOptions: { level: 6 }
    })
    
    processingSteps.value.push('Package ready for download')
    
    // Register course
    await registerCourse()
    
    // Store package in localStorage as backup (with expiry)
    // Only store metadata in localStorage to avoid quota issues
    const packageData = {
      courseId: courseId.value,
      courseTitle: courseTitle.value,
      fileName: selectedFile.value.name,
      filesModified: modifiedFilesList,
      timestamp: Date.now(),
      size: modifiedPackage.value.size
      // Note: blob kept in memory (modifiedPackage.value), not stored in localStorage
    }
    
    // Store with 24 hour expiry
    try {
      const existingPackages = JSON.parse(localStorage.getItem('echocast_processed_packages') || '[]')
      existingPackages.push(packageData)
      // Keep only packages from last 24 hours
      const recentPackages = existingPackages.filter((p: any) => Date.now() - p.timestamp < 24 * 60 * 60 * 1000)
      // Keep only last 3 packages to avoid quota issues
      localStorage.setItem('echocast_processed_packages', JSON.stringify(recentPackages.slice(-3)))
    } catch (storageError) {
      console.warn('Could not save to localStorage, clearing old data:', storageError)
      // Clear old data and try again with just this package
      try {
        localStorage.removeItem('echocast_processed_packages')
        localStorage.setItem('echocast_processed_packages', JSON.stringify([packageData]))
      } catch (e) {
        console.error('Storage quota exceeded even after clearing:', e)
        // Continue anyway - package is still processed in memory
      }
    }
    
    success.value = true
    successMessage.value = modifiedFilesList.length > 0 
      ? `Success! Modified ${modifiedFilesList.length} file${modifiedFilesList.length !== 1 ? 's' : ''}: ${modifiedFilesList.join(', ')}`
      : `Package processed successfully!`
    
    // Auto-download the modified package
    downloadPackage()
    
    // Emit success event but DON'T close modal - let user read the success message
    emit('success', {
      courseId: courseId.value,
      title: courseTitle.value,
      method: 'upload',
      toolType: selectedTool.value
    })
    
    // Don't close - user needs to read success message and click Close manually
    processing.value = false
  } catch (error) {
    console.error('Failed to process SCORM package:', error)
    processingMessage.value = 'Processing failed'
    processingDetail.value = 'An error occurred while processing your package'
    alert('Failed to process package. Please try again.')
    processing.value = false
  }
}

async function copyCode() {
  const code = ['lms', 'other'].includes(selectedTool.value)
    ? getHtmlScript()
    : getJavaScriptCode()
  
  try {
    await navigator.clipboard.writeText(code)
    success.value = true
    successMessage.value = 'Code copied to clipboard!'
    
    // Emit success
    emit('success', {
      courseId: courseId.value,
      title: courseTitle.value,
      method: 'manual',
      toolType: selectedTool.value
    })
  } catch (err) {
    alert('Failed to copy code. Please select and copy manually.')
  }
}

function downloadPackage() {
  if (!modifiedPackage.value) return
  
  const url = URL.createObjectURL(modifiedPackage.value)
  const a = document.createElement('a')
  a.href = url
  a.download = `${courseId.value}-happy-alien-enabled.zip`
  a.click()
  URL.revokeObjectURL(url)
  
  hasDownloaded.value = true
}

function handleClose() {
  // If we have success with a package, warn about losing download
  if (success.value && modifiedPackage.value && !hasDownloaded.value) {
    if (!confirm('You haven\'t downloaded your modified package yet. It will be saved for 24 hours. Are you sure you want to close?')) {
      return
    }
  }
  
  // If we have success, emit close normally
  if (success.value) {
    emit('close')
  } else if (!processing.value) {
    // Only allow close if not processing
    emit('close')
  }
}

async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result as string)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}
</script>